import React from 'react';
import { Form, FormGroup, FormControl, Button, Row, Col, Card, Table } from 'react-bootstrap';
import { Navigate } from 'react-router-dom';
import config from '../../../config';

class CurrencySettings extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            defaultCurrency: 'MYR',
            currencies: [
                { code: 'MYR', name: 'Malaysian Ringgit', rate: 1.0 },
                { code: 'USD', name: 'US Dollar', rate: 0.24 },
                { code: 'EUR', name: 'Euro', rate: 0.20 },
                { code: 'GBP', name: 'British Pound', rate: 0.17 },
                { code: 'SGD', name: 'Singapore Dollar', rate: 0.32 },
                { code: 'CNY', name: 'Chinese Yuan', rate: 1.71 },
                { code: 'JPY', name: 'Japanese Yen', rate: 26.19 },
                { code: 'THB', name: 'Thai Baht', rate: 7.86 },
                { code: 'IDR', name: 'Indonesian Rupiah', rate: 3333.33 },
                { code: 'VND', name: 'Vietnamese Dong', rate: 5476.19 }
            ],
            editingRates: {},
            loading: false
        };
    }

    componentDidMount() {
        this.fetchCurrencySettings();
    }

    fetchCurrencySettings = () => {
        const requestOptions = {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        };

        if (localStorage.getItem('user') != null) {
            const user = JSON.parse(localStorage.getItem('user'));
            fetch(`${config.apiUrl}/getCurrencySettings?company=${user.accountId}`, requestOptions)
                .then((response) => {
                    if (response.status === 404) {
                        // Settings don't exist yet, use defaults
                        return null;
                    }
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    return response.text().then(text => {
                        if (!text || text.trim() === '') {
                            return null;
                        }
                        try {
                            return JSON.parse(text);
                        } catch (e) {
                            console.error('Error parsing JSON:', e);
                            return null;
                        }
                    });
                })
                .then(data => {
                    if (data) {
                        this.setState({
                            defaultCurrency: data.defaultCurrency || 'MYR',
                            currencies: data.currencies || this.state.currencies
                        });
                    }
                })
                .catch(error => {
                    console.error('Error fetching currency settings:', error);
                    // Use default values on error
                });
        }
    }

    handleDefaultCurrencyChange = (e) => {
        this.setState({ defaultCurrency: e.target.value });
    }

    handleRateChange = (code, value) => {
        const newCurrencies = this.state.currencies.map(currency => {
            if (currency.code === code) {
                return { ...currency, rate: parseFloat(value) || 0 };
            }
            return currency;
        });
        this.setState({ currencies: newCurrencies });
    }

    handleRateEdit = (code) => {
        this.setState(prevState => ({
            editingRates: { ...prevState.editingRates, [code]: true }
        }));
    }

    handleRateSave = (code) => {
        this.setState(prevState => {
            const newEditingRates = { ...prevState.editingRates };
            delete newEditingRates[code];
            return { editingRates: newEditingRates };
        });
    }

    handleAddCurrency = () => {
        const newCode = prompt('Enter currency code (e.g., USD, EUR):');
        if (newCode && newCode.length === 3) {
            const newName = prompt('Enter currency name:') || newCode;
            const newRate = parseFloat(prompt('Enter exchange rate (relative to base currency):')) || 1.0;
            
            const currencyExists = this.state.currencies.some(c => c.code.toUpperCase() === newCode.toUpperCase());
            if (currencyExists) {
                window.alert('Currency already exists');
                return;
            }

            this.setState(prevState => ({
                currencies: [...prevState.currencies, {
                    code: newCode.toUpperCase(),
                    name: newName,
                    rate: newRate
                }]
            }));
        }
    }

    handleRemoveCurrency = (code) => {
        if (code === this.state.defaultCurrency) {
            window.alert('Cannot remove the default currency');
            return;
        }
        if (window.confirm(`Are you sure you want to remove ${code}?`)) {
            this.setState(prevState => ({
                currencies: prevState.currencies.filter(c => c.code !== code)
            }));
        }
    }

    handleSave = () => {
        if (localStorage.getItem('user') == null) {
            window.alert('User not logged in');
            return;
        }

        const user = JSON.parse(localStorage.getItem('user'));
        const requestOptions = {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                company: user.accountId,
                defaultCurrency: this.state.defaultCurrency,
                currencies: this.state.currencies
            })
        };

        this.setState({ loading: true });

        fetch(`${config.apiUrl}/saveCurrencySettings`, requestOptions)
            .then((response) => {
                return response.text().then(text => {
                    let data;
                    try {
                        data = text ? JSON.parse(text) : {};
                    } catch (e) {
                        data = {};
                    }
                    
                    if (response.status === 200) {
                        window.alert('Currency settings saved successfully');
                        this.setState({ loading: false, editingRates: {} });
                    } else {
                        const errorMsg = data.error || 'Failed to save currency settings';
                        window.alert(errorMsg);
                        this.setState({ loading: false });
                    }
                });
            })
            .catch(error => {
                console.error('Error saving currency settings:', error);
                window.alert('Failed to save currency settings. Please check your connection and try again.');
                this.setState({ loading: false });
            });
    }

    render() {
        try {
            const userData = localStorage.getItem('user');
            if (!userData) {
                return <Navigate to="/login" replace />;
            }
        } catch (error) {
            return <Navigate to="/login" replace />;
        }

        const { defaultCurrency, currencies, editingRates, loading } = this.state;

        return (
            <div className="container">
                <div style={{ marginBottom: '20px' }}>
                    <h4>Currency Settings</h4>
                    <p>Configure default currency and exchange rates for price conversions in inventory management.</p>
                </div>

                <Card style={{ marginBottom: '20px' }}>
                    <Card.Body>
                        <FormGroup>
                            <Row>
                                <Col sm={3}>
                                    <Form.Label><strong>Default Currency</strong></Form.Label>
                                </Col>
                                <Col sm={9}>
                                    <FormControl
                                        as="select"
                                        value={defaultCurrency}
                                        onChange={this.handleDefaultCurrencyChange}
                                        disabled={loading}
                                    >
                                        {currencies.map(currency => (
                                            <option key={currency.code} value={currency.code}>
                                                {currency.code} - {currency.name}
                                            </option>
                                        ))}
                                    </FormControl>
                                    <small className="text-muted" style={{ display: 'block', marginTop: '5px' }}>
                                        This currency will be used as the default when creating new inventory items.
                                    </small>
                                </Col>
                            </Row>
                        </FormGroup>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                            <h5 style={{ margin: 0 }}>Currency Exchange Rates</h5>
                            <Button 
                                variant="success" 
                                size="sm"
                                onClick={this.handleAddCurrency}
                                disabled={loading}
                            >
                                + Add Currency
                            </Button>
                        </div>
                        <p className="text-muted" style={{ fontSize: '14px', marginBottom: '15px' }}>
                            Exchange rates are relative to the base currency (USD = 1.0). Rates can be edited by clicking on the rate value.
                        </p>
                        
                        <div style={{ overflowX: 'auto' }}>
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th style={{ width: '15%' }}>Currency Code</th>
                                        <th style={{ width: '25%' }}>Currency Name</th>
                                        <th style={{ width: '20%' }}>Exchange Rate</th>
                                        <th style={{ width: '40%' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currencies.map((currency, index) => (
                                        <tr key={currency.code}>
                                            <td><strong>{currency.code}</strong></td>
                                            <td>{currency.name}</td>
                                            <td>
                                                {editingRates[currency.code] ? (
                                                    <FormControl
                                                        type="number"
                                                        step="0.0001"
                                                        min="0"
                                                        value={currency.rate}
                                                        onChange={(e) => this.handleRateChange(currency.code, e.target.value)}
                                                        style={{ width: '100%' }}
                                                        autoFocus
                                                    />
                                                ) : (
                                                    <span 
                                                        onClick={() => this.handleRateEdit(currency.code)}
                                                        style={{ 
                                                            cursor: 'pointer', 
                                                            padding: '5px 10px',
                                                            display: 'inline-block',
                                                            minWidth: '80px',
                                                            border: '1px dashed #ccc',
                                                            borderRadius: '3px'
                                                        }}
                                                        title="Click to edit"
                                                    >
                                                        {currency.rate.toFixed(4)}
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                {editingRates[currency.code] ? (
                                                    <Button
                                                        variant="success"
                                                        size="sm"
                                                        onClick={() => this.handleRateSave(currency.code)}
                                                        style={{ marginRight: '5px' }}
                                                    >
                                                        Save
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => this.handleRateEdit(currency.code)}
                                                        style={{ marginRight: '5px' }}
                                                        disabled={loading}
                                                    >
                                                        Edit Rate
                                                    </Button>
                                                )}
                                                {currency.code !== defaultCurrency && (
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => this.handleRemoveCurrency(currency.code)}
                                                        disabled={loading}
                                                    >
                                                        Remove
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>

                <div style={{ marginTop: '20px' }}>
                    <Button 
                        variant="secondary" 
                        onClick={() => this.fetchCurrencySettings()} 
                        style={{ marginRight: '10px' }}
                        disabled={loading}
                    >
                        Cancel/Refresh
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={this.handleSave}
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </div>
        );
    }
}

export { CurrencySettings };
